import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import Navigation from '../components/common/Navigation';
import Footer from '../components/common/Footer';
import MarketingHero from '../components/marketing/MarketingHero';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    preferredContact: 'email',
  });

  const supportOptions = [
    {
      title: 'Extended support hours',
      description: 'Phone and secure messaging with clear expectations on response times.',
      icon: ClockIcon,
    },
    {
      title: 'Member service team',
      description: 'Local specialists for accounts, lending questions, and digital banking help.',
      icon: UserGroupIcon,
    },
    {
      title: 'Secure communication',
      description: 'Encryption and verification practices that protect what you share with us.',
      icon: ShieldCheckIcon,
    },
  ];

  const contactChannels = [
    {
      method: 'Email support',
      value: 'support@claireoakscu.com',
      href: 'mailto:support@claireoakscu.com',
      icon: EnvelopeIcon,
      description: 'Send us your questions anytime. We reply as quickly as we can.',
      availability: 'Typically within one business day',
    },
    {
      method: 'Secure message',
      value: 'Via online banking',
      to: '/login',
      icon: ChatBubbleLeftRightIcon,
      description: 'Log in and send a secure message from your dashboard.',
      availability: 'Best for account-specific questions',
    },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const fieldClass =
    'w-full border border-oak-200 bg-mist/40 px-4 py-3.5 text-oak-900 transition focus:border-claire-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-claire-500/25';

  return (
    <div className="min-h-screen bg-mist text-oak-900">
      <Navigation />

      <MarketingHero
        title="We are here to help"
        titleAccent="every step of the way"
        description="Questions about membership, applications, or digital banking? Reach out. We will connect you with the right local specialist."
      />

      <section className="relative z-10 -mt-8 px-4 sm:-mt-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-5 md:grid-cols-2">
          {contactChannels.map((channel) => (
            <div
              key={channel.method}
              className="panel-lift border border-oak-100 p-8 shadow-lg"
            >
              <div className="flex h-12 w-12 items-center justify-center bg-oak-900 text-claire-200">
                <channel.icon className="h-6 w-6" aria-hidden />
              </div>
              <h3 className="mt-6 font-display text-2xl font-semibold text-oak-900">{channel.method}</h3>
              <p className="mt-3 text-oak-600">{channel.description}</p>
              {channel.to ? (
                <Link to={channel.to} className="mt-5 inline-flex items-center font-semibold text-claire-700 hover:text-claire-800">
                  {channel.value}
                  <ArrowRightIcon className="ml-2 h-4 w-4" aria-hidden />
                </Link>
              ) : (
                <a href={channel.href} className="mt-5 inline-flex items-center font-semibold text-claire-700 hover:text-claire-800">
                  {channel.value}
                  <ArrowRightIcon className="ml-2 h-4 w-4" aria-hidden />
                </a>
              )}
              <p className="mt-5 border-t border-oak-100 pt-4 text-sm text-oak-500">{channel.availability}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-leaf-vein py-20 sm:py-24">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 lg:grid-cols-5 lg:gap-14 sm:px-6 lg:px-8">
          <div className="lg:col-span-2">
            <div className="section-rule mb-5" />
            <h2 className="font-display text-3xl font-semibold text-oak-900 sm:text-4xl">Visit our office</h2>
            <p className="mt-4 text-lg text-oak-600">
              Prefer an in-person conversation? Stop by. We will help you find the right accounts and loans.
            </p>
            <div className="mt-8 space-y-5 border border-oak-100 bg-white/80 p-6 shadow-soft backdrop-blur-sm">
              <div className="flex gap-3">
                <MapPinIcon className="mt-0.5 h-5 w-5 shrink-0 text-claire-600" aria-hidden />
                <div>
                  <p className="font-semibold text-oak-900">Main office</p>
                  <p className="mt-1 text-oak-600">6255 Carrsville Hwy, Carrsville, VA 23315</p>
                </div>
              </div>
              <div className="flex gap-3">
                <EnvelopeIcon className="mt-0.5 h-5 w-5 shrink-0 text-claire-600" aria-hidden />
                <div>
                  <p className="font-semibold text-oak-900">Email</p>
                  <a href="mailto:support@claireoakscu.com" className="mt-1 text-claire-700 hover:text-claire-800">
                    support@claireoakscu.com
                  </a>
                </div>
              </div>
              <p className="border-t border-oak-100 pt-4 text-sm text-oak-500">
                Federally insured by NCUA. Equal opportunity lender. NMLS ID: 6488745.
              </p>
            </div>
          </div>

          <div className="border border-oak-100 bg-white p-8 shadow-soft lg:col-span-3 sm:p-10">
            <h2 className="font-display text-2xl font-semibold text-oak-900 sm:text-3xl">Send us a message</h2>
            <p className="mt-3 text-oak-600">
              Share a few details and we will route your note to the right team.
            </p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-oak-700" htmlFor="contact-name">
                    Name
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    className={fieldClass}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-oak-700" htmlFor="contact-email">
                    Email
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    className={fieldClass}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-oak-700" htmlFor="contact-subject">
                  Subject
                </label>
                <select
                  id="contact-subject"
                  className={fieldClass}
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                >
                  <option value="">Select a subject</option>
                  <option value="general">General inquiry</option>
                  <option value="account">Account support</option>
                  <option value="loan">Loan information</option>
                  <option value="technical">Technical support</option>
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-oak-700" htmlFor="contact-message">
                  Message
                </label>
                <textarea
                  id="contact-message"
                  className={fieldClass}
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                />
              </div>

              <div>
                <span className="mb-3 block text-sm font-medium text-oak-700">Preferred contact method</span>
                <div className="flex flex-wrap gap-6">
                  <label className="flex cursor-pointer items-center">
                    <input
                      type="radio"
                      name="contact"
                      value="email"
                      checked={formData.preferredContact === 'email'}
                      onChange={(e) => setFormData({ ...formData, preferredContact: e.target.value })}
                      className="text-claire-600 focus:ring-claire-500/30"
                    />
                    <span className="ml-2 text-oak-700">Email</span>
                  </label>
                  <label className="flex cursor-pointer items-center">
                    <input
                      type="radio"
                      name="contact"
                      value="phone"
                      checked={formData.preferredContact === 'phone'}
                      onChange={(e) => setFormData({ ...formData, preferredContact: e.target.value })}
                      className="text-claire-600 focus:ring-claire-500/30"
                    />
                    <span className="ml-2 text-oak-700">Phone</span>
                  </label>
                </div>
              </div>

              <button
                type="submit"
                className="inline-flex w-full items-center justify-center bg-oak-900 px-6 py-3.5 text-base font-semibold text-white transition hover:-translate-y-0.5 hover:bg-claire-700 sm:w-auto"
              >
                Send message
                <ArrowRightIcon className="ml-2 h-5 w-5" aria-hidden />
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="border-t border-oak-100 bg-white py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            {supportOptions.map((option) => (
              <div key={option.title} className="border border-oak-100 bg-mist/40 p-8 transition hover:border-claire-200 hover:bg-white hover:shadow-soft">
                <div className="flex h-11 w-11 items-center justify-center bg-oak-900 text-claire-200">
                  <option.icon className="h-5 w-5" aria-hidden />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold text-oak-900">{option.title}</h3>
                <p className="mt-3 text-oak-600">{option.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
